import { semanticColorsDarkTheme, semanticColorsLightTheme, type SemanticColors } from "./color";
import { readInitialIsDark } from "./themePreferences";

export const semanticThemes = {
  light: semanticColorsLightTheme,
  dark: semanticColorsDarkTheme,
} as const;

export type ThemeMode = keyof typeof semanticThemes;

function applySemanticThemeToRoot(semanticColors: SemanticColors, mode: ThemeMode): void {
  const root = document.documentElement;

  (Object.keys(semanticColors) as Array<keyof SemanticColors>).forEach((key) => {
    root.style.setProperty(`--${key}`, semanticColors[key]);
  });

  root.style.colorScheme = mode;
  root.dataset.theme = mode;
}

/** Returns whether semantic theme variables are already on `:root`. */
export function isThemeLoaded(): boolean {
  if (typeof document === "undefined") {
    return false;
  }

  const mode = document.documentElement.dataset.theme;
  return mode === "light" || mode === "dark";
}

/**
 * Applies semantic theme CSS variables to `document.documentElement`.
 * Defaults to saved preference or system color scheme when `isDark` is omitted.
 */
export function loadTheme(isDark?: boolean): boolean {
  const resolvedIsDark = isDark ?? readInitialIsDark();
  const mode: ThemeMode = resolvedIsDark ? "dark" : "light";

  if (typeof document === "undefined") {
    if (process.env.NODE_ENV === "development") {
      console.log("[theme] loadTheme skipped — no document (SSR/build)");
    }
    return resolvedIsDark;
  }

  if (process.env.NODE_ENV === "development") {
    console.log("[theme] loadTheme applied", mode);
  }

  applySemanticThemeToRoot(semanticThemes[mode], mode);
  return resolvedIsDark;
}
